import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import SimpleDragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item-simple';
import { DragDropItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { UnfilterableIcon } from '@components-clean/core/icons/unfilterable-icon/unfilterable-icon';
import { PanelFilteringDialog } from '@pages-clean/admin/study/admin-data-views/data-views/dialogs/panel-filtering-dialog';
import {
    DataFilterDragDropDataItem,
    DataFilterDragDropItem,
    DataFilterDragDropItemProps,
} from '@pages-clean/admin/study/admin-data-views/data-views/filters/data-filter-drag-drop-item';
import {
    DataViewConfig,
    DataViewConfigForm,
    DragDropListPanelData,
    IdTitleActive,
    PanelData,
} from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { DataFilterResultType } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { DataFilterSearch, mapDataFilterToDragDrop } from '@pages-clean/admin/study/admin-data-views/utils';
import { PanelType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { DataReviewTableFields } from '@queries/generated/fragments.generated';

enum ChipValues {
    Panels = 1,
    DataTables = 2,
    DataFilters = 3,
}

type Props = {
    canEdit: boolean;
    activeDataView: DataViewConfig;
    studyPanels: PanelData[];
    studyDataTables: DataReviewTableFields[];
    dataViewFilters: DataFilterResultType[];
};

type SelectedAvailable<T> = {
    selected: T[];
    available: T[];
};

export const EditableDataViewConfigForm = ({
    canEdit,
    activeDataView,
    studyPanels,
    studyDataTables,
    dataViewFilters,
}: Props) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const [chip, setChip] = useState<ChipValues>(ChipValues.Panels);
    const [showPanelFilteringDialog, setShowPanelFilteringDialog] = useState<boolean>(false);
    const {
        setValue,
        getValues,
        watch,
        formState: { isDirty },
    } = useFormContext<DataViewConfigForm>();
    const watchMainPanel = watch('mainPanel');
    const watchUnfilterablePanelIds = watch('unfilterablePanelIds');

    // Using form values instead of DataView prop, as if the very first action is done in
    // a drag and drop list, setting the form to dirty causes a re-render
    // If using the Data View prop, it will re-render the columns without the newly added value
    const dataViewForm = getValues();

    useEffect(() => {
        if (!watchMainPanel || !dataViewForm.panelIds) return;

        // Filter out the newly selected main panel from the selected list, updating the form value
        const index = dataViewForm.panelIds.indexOf(watchMainPanel?.id);
        if (index > -1) setValue('panelIds', dataViewForm.panelIds.toSpliced(index, 1));
    }, [watchMainPanel]);

    // Bit of a hack. The very first edit action causes a re-render, as it sets formState.isDirty
    // If the first action is to move an item up/down it will re-render, causing the item to not be selected
    // as it would re-calculate the data source which doesn't track the selected state.
    // However if we memoize it, the data source doesn't change, meaning the drag drop list datasource wont be re-written,
    // and it tracks and maintains its own state. We will need to re-calculate it when swapping chips
    // Soooooooo this means we can move items up/down and still have it selected after the re-render caused by the form
    const sortPanelsIntoList = useMemo(() => {
        if (!dataViewForm.panelIds)
            return {
                selected: [],
                available: studyPanels.filter((p) => p.id === dataViewForm.mainPanel?.id),
            };

        const panels = studyPanels.reduce<SelectedAvailable<DragDropListPanelData>>(
            (listItems, panel) => {
                if (panel.id === watchMainPanel?.id) return listItems;
                if (panel.panelType === PanelType.EVENT_RATES_OVER_TIME) return listItems;
                if (dataViewForm.panelIds!.indexOf(panel.id) > -1) {
                    listItems.selected.push(panel);
                    // Include inactive panels that were originally selected
                } else if (panel.active || activeDataView.panelIds.indexOf(panel.id) > -1) {
                    listItems.available.push(panel);
                }

                return listItems;
            },
            { selected: [], available: [] },
        );

        // Sort the selected into the same order as the dataViewForm.panelIds
        panels.selected.sort((a, b) => dataViewForm.panelIds!.indexOf(a.id) - dataViewForm.panelIds!.indexOf(b.id));
        return panels;
    }, [studyPanels, chip, watchMainPanel]);

    // See hack comment above
    const sortDataTablesIntoList = useMemo(() => {
        const result = studyDataTables.reduce<SelectedAvailable<IdTitleActive>>(
            (listItems, dataTable) => {
                const item = {
                    id: dataTable.id as string,
                    title: dataTable.name ?? '',
                    active: dataTable.active ?? false,
                };
                const index = dataViewForm.dataTableIds?.indexOf(dataTable.id as string);
                if (index !== undefined && index > -1) listItems.selected.push(item);
                // Include inactive data tables that were originally selected
                else if (item.active || activeDataView.dataTableIds.indexOf(item.id) > -1)
                    listItems.available.push(item);

                return listItems;
            },
            { selected: [], available: [] },
        );

        result.selected.sort(
            (a, b) => dataViewForm.dataTableIds!.indexOf(a.id) - dataViewForm.dataTableIds!.indexOf(b.id),
        );

        return result;
    }, [studyDataTables, chip]);

    // See hack comment above
    const sortFiltersIntoList = useMemo(() => {
        const result = dataViewFilters
            .filter((f) => f.dataSource === dataViewForm.dataSource)
            .reduce<SelectedAvailable<DataFilterDragDropDataItem>>(
                (listItems, dataFilter) => {
                    const item = mapDataFilterToDragDrop(dataFilter, state);

                    const index = dataViewForm.dataFilterIds?.indexOf(dataFilter.id as string);
                    if (index !== undefined && index > -1) listItems.selected.push(item);
                    else listItems.available.push(item);

                    return listItems;
                },
                {
                    selected: [],
                    available: [],
                },
            );

        result.selected.sort(
            (a, b) => dataViewForm.dataFilterIds!.indexOf(a.id) - dataViewForm.dataFilterIds!.indexOf(b.id),
        );
        result.available.sort((a, b) => a.title.localeCompare(b.title));

        return result;
    }, [dataViewFilters, chip]);

    const panelLists = sortPanelsIntoList;
    const dataTables = sortDataTablesIntoList;
    const dataFilters = sortFiltersIntoList;

    const onPanelExceptionsClick = () => {
        setShowPanelFilteringDialog(true);
    };

    const renderItem = ({ dataItem, ...rest }: DragDropItemProps) => {
        let suffix: ReactNode | undefined;
        const id = dataItem.id as string;
        const isSelected = dataViewForm.panelIds?.includes(id);
        const showSuffix = watchUnfilterablePanelIds?.includes(id);
        if (isSelected && showSuffix) {
            suffix = (
                <UnfilterableIcon
                    dataTestId={`${id}-unfilterable`}
                    defaultOpacity={75}
                    tooltip={t('cm:componentsClean.panels.screenFilterableWarning')}
                    tooltipPosition="bottom"
                />
            );
        }

        return <SimpleDragDropItem {...rest} dataItem={{ ...dataItem, suffix }} toDisplayColumn />;
    };

    return (
        <>
            <Card
                data-testid="editable-data-view-config-lists"
                style={{ minWidth: '500px', height: '100%', minHeight: '200px' }}
                title={
                    <ChipList
                        // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                        // some rerenders to steal focus from input elements
                        key={String(isDirty)}
                        data-testid="editable-data-view-config-list-header"
                        defaultData={[
                            { text: t('admin:panels'), value: ChipValues.Panels },
                            { text: t('admin:dataTables'), value: ChipValues.DataTables },
                            { text: t('admin:dataViewFilters'), value: ChipValues.DataFilters },
                        ]}
                        value={chip}
                        onChange={(event) => {
                            if (event.value !== null) setChip(event.value as ChipValues);
                        }}
                        selection="single"
                    />
                }
            >
                <div className="k-h-full">
                    {chip === ChipValues.Panels && (
                        <DragDropListsCore
                            list1Header={t('study:panelsDisplay')}
                            list2Header={t('study:availablePanels')}
                            list1Items={panelLists.selected}
                            list2Items={panelLists.available}
                            idField="id"
                            onChangeItemsLeft={(values) => {
                                const selectedIds = values.map((p) => p.id as string);
                                setValue('panelIds', selectedIds, {
                                    shouldDirty: true,
                                });

                                // Update the unfilterablePanelIds. Any unselected panel should be removed, and any
                                // model panels should be added by default.
                                const modelPanels = values.filter((p) => p.isModel).map((p) => p.id as string);
                                modelPanels.forEach((id) => {
                                    if (!watchUnfilterablePanelIds?.includes(id)) watchUnfilterablePanelIds?.push(id);
                                });
                                setValue(
                                    'unfilterablePanelIds',
                                    watchUnfilterablePanelIds?.filter(
                                        (id) => selectedIds.includes(id) || id === watchMainPanel?.id,
                                    ),
                                    { shouldDirty: true },
                                );
                            }}
                            isTwoLists
                            disabled={!canEdit}
                            list1HeaderActions={
                                <Button
                                    primary
                                    data-testid="unfilterable-panels-button"
                                    onClick={onPanelExceptionsClick}
                                    faIcon={['far', 'filter']}
                                    faIconStyle={{ width: '13px', height: '13px' }}
                                >
                                    {t('admin:dataView.panelFilterExceptionsBtn')}
                                </Button>
                            }
                            renderItemLeft={renderItem}
                            renderItemRight={renderItem}
                        />
                    )}
                    {chip === ChipValues.DataTables && (
                        <DragDropListsCore
                            list1Header={t('study:dataTablesToDisplay')}
                            list2Header={t('study:availableDataTables')}
                            list1Items={dataTables.selected}
                            list2Items={dataTables.available}
                            idField="id"
                            onChangeItemsLeft={(values) => {
                                setValue(
                                    'dataTableIds',
                                    values.map((p) => p.id as string),
                                    {
                                        shouldDirty: true,
                                    },
                                );
                            }}
                            isTwoLists
                            disabled={!canEdit}
                        />
                    )}
                    {chip === ChipValues.DataFilters && (
                        <DragDropListsCore
                            list1Header={t('study:dataFiltersToDisplay')}
                            list2Header={t('study:availableDataFilters')}
                            list1Items={dataFilters.selected}
                            list2Items={dataFilters.available}
                            idField="id"
                            onChangeItemsLeft={(values) => {
                                setValue(
                                    'dataFilterIds',
                                    values.map((p) => p.id as string),
                                    {
                                        shouldDirty: true,
                                    },
                                );
                            }}
                            isTwoLists
                            disabled={!canEdit}
                            renderItemLeft={(item) => {
                                return (
                                    <DataFilterDragDropItem
                                        inactiveIconTooltip={t('admin:fieldNoLongerInUse')}
                                        toDisplayColumn
                                        {...(item as DataFilterDragDropItemProps)}
                                    />
                                );
                            }}
                            renderItemRight={(item) => {
                                return (
                                    <DataFilterDragDropItem
                                        inactiveIconTooltip={t('admin:fieldNoLongerInUse')}
                                        toDisplayColumn
                                        {...(item as DataFilterDragDropItemProps)}
                                    />
                                );
                            }}
                            filterItem={DataFilterSearch}
                        />
                    )}
                </div>
            </Card>
            {showPanelFilteringDialog && (
                <PanelFilteringDialog
                    onClose={() => setShowPanelFilteringDialog(false)}
                    onSubmit={(value) => {
                        setValue('unfilterablePanelIds', value, { shouldDirty: true });
                        setShowPanelFilteringDialog(false);
                    }}
                    studyPanels={studyPanels}
                    selectedPanels={getValues('panelIds') ?? []}
                    mainPanel={dataViewForm.mainPanel}
                    nonFilterablePanelIds={watchUnfilterablePanelIds ?? []}
                />
            )}
        </>
    );
};
